import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPrintletDashboard, runPrintletSync } from "../../api/PrintletAPIs";
import { KpiCard, SectionCard } from "../Analytics/AnalyticsUi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Send,
  Factory,
  Truck,
  CheckCircle2,
  RotateCcw,
  XCircle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Clock,
  Wallet,
  Package,
  DollarSign,
  Info,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, accent = "slate", tip }) {
  const accents = {
    indigo:  "bg-indigo-50 text-indigo-600 border-indigo-100",
    sky:     "bg-sky-50 text-sky-600 border-sky-100",
    violet:  "bg-violet-50 text-violet-600 border-violet-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber:   "bg-amber-50 text-amber-600 border-amber-100",
    rose:    "bg-rose-50 text-rose-600 border-rose-100",
    slate:   "bg-slate-50 text-slate-600 border-slate-100",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className={`p-2 rounded-xl border ${accents[accent]}`}>
          <Icon size={18} />
        </div>
        {tip && (
          <span title={tip} className="text-slate-300 hover:text-slate-500 cursor-help">
            <Info size={14} />
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-800 leading-tight">{value}</p>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, accent = "slate" }) {
  const accents = {
    indigo: "text-indigo-500",
    emerald: "text-emerald-500",
    amber: "text-amber-500",
    rose: "text-rose-500",
    slate: "text-slate-400",
  };
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Icon size={15} className={accents[accent]} />
        {label}
      </div>
      <div className="font-semibold text-slate-800">{value}</div>
    </div>
  );
}

export default function PrintletOverviewPage() {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["printletDashboard"],
    queryFn: () => fetchPrintletDashboard(),
  });

  const [syncing, setSyncing] = React.useState(false);

  const sync = async () => {
    setSyncing(true);
    try {
      await runPrintletSync();
      toast.success("تمت المزامنة بنجاح");
      refetch();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4 h-28 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const d = data || {};

  return (
    <div className="space-y-5">
      <ToastContainer position="top-center" />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">نظرة عامة</h2>
          <p className="text-sm text-slate-500 mt-1">ملخص أداء Printlet ومؤشرات التشغيل</p>
        </div>
        <button
          onClick={sync}
          disabled={syncing || isFetching}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition"
        >
          <RefreshCw size={16} className={syncing || isFetching ? "animate-spin" : ""} />
          مزامنة Printlet
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Send}         label="أُرسلت"            value={d.totalSent ?? 0}      accent="indigo" />
        <StatCard icon={Factory}      label="قيد الإنتاج"        value={d.inProduction ?? 0}   accent="sky" />
        <StatCard icon={Truck}        label="شُحنت"             value={d.shipped ?? 0}        accent="violet" />
        <StatCard icon={CheckCircle2} label="سُلّمت"            value={d.delivered ?? 0}      accent="emerald" />
        <StatCard icon={RotateCcw}    label="مرتجعة"            value={d.returned ?? 0}       accent="amber" />
        <StatCard icon={XCircle}      label="فشل"               value={d.failed ?? 0}         accent="rose" />
        <StatCard
          icon={TrendingUp}
          label="نسبة التسليم"
          value={`${d.deliveryRate ?? 0}%`}
          accent="emerald"
          tip="محسوبة من الطلبات المُرسلة محلياً بعد المزامنة"
        />
        <StatCard icon={TrendingDown} label="نسبة الإرجاع"      value={`${d.returnRate ?? 0}%`} accent="amber" />
      </div>

      {/* Two-column layout */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Operations */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-xl bg-sky-50 text-sky-600 border border-sky-100">
              <Clock size={18} />
            </div>
            <h3 className="font-bold text-slate-800">مؤشرات التشغيل</h3>
          </div>
          <div className="space-y-0">
            <InfoRow
              icon={Clock}
              label="متوسط وقت الشحن"
              value={d.averageHoursToShipment != null ? `${d.averageHoursToShipment} ساعة` : "—"}
              accent="indigo"
            />
            <InfoRow
              icon={Clock}
              label="متوسط وقت التسليم"
              value={d.averageHoursToDelivery != null ? `${d.averageHoursToDelivery} ساعة` : "—"}
              accent="emerald"
            />
          </div>
        </div>

        {/* Financials */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-xl bg-amber-50 text-amber-600 border border-amber-100">
              <Wallet size={18} />
            </div>
            <h3 className="font-bold text-slate-800">المالية</h3>
            <span className="text-xs text-slate-400 mr-auto">(حسب ما يعرضه Printlet)</span>
          </div>
          <div className="space-y-0">
            <InfoRow
              icon={Package}
              label="COD المتوقع (مُرسل من جراباتكم)"
              value={Number(d.codExpected ?? 0).toLocaleString()}
              accent="amber"
            />
            <InfoRow
              icon={DollarSign}
              label="تكلفة التنفيذ Printlet"
              value={Number(d.printletFulfillmentCost ?? 0).toLocaleString()}
              accent="indigo"
            />
            <InfoRow
              icon={Truck}
              label="تكلفة الشحن Printlet"
              value={Number(d.printletShippingCost ?? 0).toLocaleString()}
              accent="violet"
            />
          </div>
          {d.walletNote && (
            <p className="text-xs text-slate-500 mt-3 p-2 bg-slate-50 rounded-lg">{d.walletNote}</p>
          )}
        </div>
      </div>
    </div>
  );
}